import React, { useState, useEffect } from 'react';
import { useAppContext } from '../AppContext';
import { doc, onSnapshot, updateDoc, collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import LoadingSpinner from '../components/LoadingSpinner';
import { motion, AnimatePresence, animate } from 'framer-motion';
import { CheckCircle, Circle, Swords } from 'lucide-react';

const AnimatedNumber = ({ number }) => {
    const ref = React.useRef(null);

    useEffect(() => {
        const controls = animate(0, number, {
            duration: 1,
            onUpdate(value) {
                if (ref.current) {
                    ref.current.textContent = Math.round(value);
                }
            }
        });
        return () => controls.stop();
    }, [number]);

    return <span ref={ref}>0</span>;
};

const ReadyUpPage = ({ challengeId, setView, setSelectedMatch }) => {
    const { db, user } = useAppContext();
    const [challenge, setChallenge] = useState(null);
    const [rivalryRecord, setRivalryRecord] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!db || !challengeId) return;

        const challengeRef = doc(db, 'artifacts/whispers-of-harmony/public/data/challenges', challengeId);
        const unsubscribe = onSnapshot(challengeRef, (doc) => {
            if (doc.exists()) {
                const challengeData = { id: doc.id, ...doc.data() };
                setChallenge(challengeData);

                if (challengeData.challengerReady && challengeData.challengedReady) {
                    const isParticipant = user.uid === challengeData.challengerId || user.uid === challengeData.challengedId;
                    if (isParticipant) {
                        setView('arena');
                    } else {
                        setSelectedMatch(challengeData);
                        setView('spectateMatch');
                    }
                }
            } else {
                setError('Challenge not found.');
            }
            setLoading(false);
        }, (err) => {
            setError(err);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [db, challengeId, setView, setSelectedMatch]);

    useEffect(() => {
        if (!challenge) return;

        const fetchRivalryRecord = async () => {
            const challengesRef = collection(db, 'artifacts/whispers-of-harmony/public/data/challenges');
            const q = query(challengesRef,
                where('status', '==', 'completed'),
                where('participants', 'array-contains', challenge.challengerId),
                orderBy('createdAt', 'desc'),
                limit(20)
            );
            
            const querySnapshot = await getDocs(q);
            let challengerWins = 0;
            let challengedWins = 0;

            querySnapshot.forEach(doc => {
                const match = doc.data();
                if (match.participants.includes(challenge.challengedId)) {
                    if (match.winnerId === challenge.challengerId) {
                        challengerWins++;
                    } else if (match.winnerId === challenge.challengedId) {
                        challengedWins++;
                    }
                }
            });
            setRivalryRecord({ challenger: challengerWins, challenged: challengedWins });
        };

        fetchRivalryRecord();
    }, [db, challenge]);

    const handleReadyUp = async () => {
        if (!challenge) return;
        const isChallenger = user.uid === challenge.challengerId;
        const challengeRef = doc(db, 'artifacts/whispers-of-harmony/public/data/challenges', challengeId);
        
        if (isChallenger) {
            await updateDoc(challengeRef, { challengerReady: true });
        } else {
            await updateDoc(challengeRef, { challengedReady: true });
        }
    };

    if (loading) return <LoadingSpinner message="Preparing Match..." />;
    if (error) return <p className="text-red-500 p-4">{error}</p>;
    if (!challenge) return null;

    const isChallenger = user.uid === challenge.challengerId;
    const userIsReady = isChallenger ? challenge.challengerReady : challenge.challengedReady;

    const PlayerStatus = ({ name, isReady, isUser }) => (
        <div className="w-full md:w-1/3 bg-gray-800/40 backdrop-blur-md border border-white/10 p-8 rounded-xl text-center shadow-xl transition-all hover:bg-gray-800/50">
            <h2 className="text-2xl font-bold text-white font-orbitron mb-2">{name}</h2>
            <div className="flex items-center justify-center mt-4 h-12">
                <AnimatePresence mode="wait">
                    {isReady ? (
                        <motion.div
                            key="ready"
                            initial={{ opacity: 0, scale: 0.5 }}
                            animate={{ opacity: 1, scale: 1, transition: { type: 'spring', stiffness: 500, damping: 30 } }}
                            exit={{ opacity: 0, scale: 0.5 }}
                            className="flex flex-col items-center gap-2"
                        >
                            <div className="p-2 bg-green-500/20 rounded-full">
                                <CheckCircle className="text-green-400" size={32} />
                            </div>
                            <p className="text-green-400 font-bold uppercase tracking-widest text-sm">Ready</p>
                        </motion.div>
                    ) : (
                        <motion.div
                            key="waiting"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="flex flex-col items-center gap-2"
                        >
                            <Circle className="text-gray-500 animate-pulse" size={32} />
                            <p className="text-gray-500 font-bold uppercase tracking-widest text-sm">Waiting...</p>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
            {isUser && !isReady && (
                <button onClick={handleReadyUp} className="mt-8 w-full px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 rounded-lg font-bold text-white shadow-lg shadow-indigo-500/30 transition-transform hover:scale-105 border border-white/10">
                    READY UP
                </button>
            )}
        </div>
    );

    const cardVariants = {
        hidden: (isLeft) => ({ x: isLeft ? -100 : 100, opacity: 0 }),
        visible: { x: 0, opacity: 1, transition: { type: 'spring', stiffness: 50 } },
    };

    return (
        <div className="w-full h-full bg-gradient-to-b from-slate-900/90 to-black/95 backdrop-blur-xl text-white p-4 md:p-8 flex flex-col items-center justify-center overflow-hidden">
            <motion.h1 
                className="text-4xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500 font-orbitron mb-12 drop-shadow-lg"
                initial={{ y: -50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
            >
                Match Found
            </motion.h1>
            <div className="flex flex-col md:flex-row items-center justify-around w-full gap-8 max-w-5xl">
                <motion.div custom={true} variants={cardVariants} initial="hidden" animate="visible" className="w-full md:w-1/3">
                    <PlayerStatus name={challenge.challengerName} isReady={challenge.challengerReady} isUser={isChallenger} />
                </motion.div>
                
                <motion.div 
                    className="text-center flex flex-col items-center gap-4"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.6 }}
                >
                    {rivalryRecord ? (
                        <>
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Rivalry Record</p>
                            <div className="bg-black/40 backdrop-blur-sm border border-white/10 px-8 py-4 rounded-2xl">
                                <p className="text-5xl font-bold text-white font-orbitron flex items-center gap-4">
                                    <span className="text-cyan-400"><AnimatedNumber number={rivalryRecord.challenger} /></span>
                                    <span className="text-gray-600 text-3xl">-</span>
                                    <span className="text-purple-400"><AnimatedNumber number={rivalryRecord.challenged} /></span>
                                </p>
                            </div>
                        </>
                    ) : (
                        <div className="p-6 bg-gray-800/40 rounded-full border border-white/10">
                            <Swords className="text-gray-400" size={48} />
                        </div>
                    )}
                </motion.div>

                <motion.div custom={false} variants={cardVariants} initial="hidden" animate="visible" className="w-full md:w-1/3">
                    <PlayerStatus name={challenge.challengedName} isReady={challenge.challengedReady} isUser={!isChallenger} />
                </motion.div>
            </div>
        </div>
    );
};

export default ReadyUpPage;
